import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Eye, Edit3, Trash2 } from 'lucide-react';
import EavList from '../components/EavList';
import UserSelect from '../components/UserSelect';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';
import ResponsiblePersonsCell from '../components/ResponsiblePersonsCell';
import BatchAssignResponsibleModal from '../components/BatchAssignResponsibleModal';
import BatchRemoveResponsibleModal from '../components/BatchRemoveResponsibleModal';
import BatchUpdateModal from '../components/BatchUpdateModal';

const SupplierList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const [rpLookup, setRpLookup] = useState(null);
  const [batchAssignIds, setBatchAssignIds] = useState(null);
  const [batchRemoveIds, setBatchRemoveIds] = useState(null);
  const [batchUpdateIds, setBatchUpdateIds] = useState(null);

  const afterLoad = useCallback((items) => {
    const ids = items.map(i => i.entity_id).filter(id => id != null);
    if (ids.length === 0) { setRpLookup({}); return; }
    const params = new URLSearchParams();
    ids.forEach(id => params.append('ids[]', id));
    fetch(`/api/suppliers/batch-responsible-persons?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') setRpLookup(data.data || {});
      })
      .catch(() => {});
  }, []);

  const renderCell = useCallback((item, col) => {
    if (col.code === 'responsible_persons') {
      return (
        <ResponsiblePersonsCell
          entityType="supplier"
          entityId={item.entity_id}
          lookup={rpLookup}
        />
      );
    }
    return null;
  }, [rpLookup]);

  const flatColumns = [
    { code: 'entity_id', label: t('products.id'), inputType: 'Text Field' },
    { code: 'attribute_set_id', label: t('products.attribute_set'), inputType: 'Text Field' },
    { code: 'email', label: t('common.email'), inputType: 'Text Field' },
    { code: 'responsible_persons', label: t('products.responsible_person'), inputType: 'Text Field' },
    { code: 'created_at', label: t('products.created_at'), inputType: 'Date' },
    { code: 'updated_at', label: t('products.updated_at'), inputType: 'Date' },
    { code: 'created_by', label: t('products.created_by'), inputType: 'Text Field' },
  ];

  const filterCustomRenderers = {
    responsible_persons: (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    ),
    created_by: (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    ),
    created_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          value={allFilters?.['created_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_from: e.target.value})}
          placeholder={t('common.from_date')}
        />
        <input
          type="date"
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          value={allFilters?.['created_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_to: e.target.value})}
          placeholder={t('common.to_date')}
        />
      </div>
    ),
    updated_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          value={allFilters?.['updated_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_from: e.target.value})}
          placeholder={t('common.from_date')}
        />
        <input
          type="date"
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          value={allFilters?.['updated_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_to: e.target.value})}
          placeholder={t('common.to_date')}
        />
      </div>
    ),
  };

  const navigate = useNavigate();
  const canAssign = hasPermission('supplier', 'assign');

  const renderActions = (item, helpers) => {
    const { canView, canEdit, canDelete, handleDelete } = helpers;
    return (
      <>
        {canView && (
          <button onClick={() => navigate(`/suppliers/view/${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title={t("common.view")}><Eye size={16} /></button>
        )}
        {canAssign && (
          <button onClick={() => navigate(`/suppliers/${item.entity_id}/assign`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
            title={t("common.batch_assign_responsible")}><UserPlus size={16} /></button>
        )}
        {canEdit && (
          <button onClick={() => navigate(`/suppliers/edit/${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t("common.edit")}><Edit3 size={16} /></button>
        )}
        {canDelete && (
          <button onClick={() => handleDelete(item.entity_id)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t("common.delete")}><Trash2 size={16} /></button>
        )}
      </>
    );
  };

  return (
    <>
    <EavList
      entityType="suppliers"
      attributeSetId={2}
      flatColumns={flatColumns}
      defaultVisible={['entity_id', 'email', 'company_name', 'phone_number', 'website']}
      titleKey="suppliers.title"
      searchKey="suppliers.search_placeholder"
      pageTotalKey="products.page_total"
      confirmDeleteKey="suppliers.confirm_delete"
      confirmDeleteSingleKey="suppliers.confirm_delete_single"
      usePageSizeChange={true}
      filterCustomRenderers={filterCustomRenderers}
      routes={{
        add: '/suppliers/new',
        view: (id) => `/suppliers/view/${id}`,
        edit: (id) => `/suppliers/edit/${id}`,
      }}
      deleteEndpoint={(id) => `/api/suppliers/${id}/delete`}
      canAdd={hasPermission('supplier', 'add')}
      canEdit={hasPermission('supplier', 'edit')}
      canDelete={hasPermission('supplier', 'delete')}
      canView={hasPermission('supplier', 'view')}
      renderActions={renderActions}
      renderCell={renderCell}
      afterLoad={afterLoad}
      onBatchAssign={canAssign ? setBatchAssignIds : undefined}
      onBatchRemoveResponsible={canAssign ? setBatchRemoveIds : undefined}
      onBatchUpdateCustom={setBatchUpdateIds}
    />
      {batchAssignIds && (
        <BatchAssignResponsibleModal
          entityType="supplier"
          selectedIds={batchAssignIds}
          onClose={() => setBatchAssignIds(null)}
          onSuccess={() => {
            setBatchAssignIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {batchRemoveIds && (
        <BatchRemoveResponsibleModal
          entityType="supplier"
          selectedIds={batchRemoveIds}
          onClose={() => setBatchRemoveIds(null)}
          onSuccess={() => {
            setBatchRemoveIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
      {batchUpdateIds && (
        <BatchUpdateModal
          entityType="suppliers"
          selectedIds={batchUpdateIds}
          onClose={() => setBatchUpdateIds(null)}
          onSuccess={() => {
            setBatchUpdateIds(null);
            setTimeout(() => setRpLookup(null), 100);
          }}
        />
      )}
    </>
  );
};

export default SupplierList;
